import React, { useState, useRef, useEffect } from 'react';
import { Building2, Phone, MapPin, Download, Upload, Trash2, FileText, AlertTriangle, Copy, Plus, ChevronDown, ChevronUp, CreditCard, CheckCircle, Clock, XCircle, Bell, BellOff, Printer, Lock, UserCheck, UserX, Users, Edit as EditIcon } from 'lucide-react';
import { differenceInDays, parseISO, format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { getAllGymNotices, createGymNotice, updateGymNotice, deleteGymNotice } from '../lib/gymNotices';
import { submitPaymentRequest, getMyPaymentRequests } from '../lib/paymentRequests';
import { isPushSupported, getNotificationPermission, getCurrentSubscription, requestAndSubscribe, unsubscribeFromPush } from '../lib/pushNotifications';
import { printPaymentReceipt } from '../lib/invoice';
import { supabase } from '../lib/supabase';
import ConfirmDialog from '../components/ui/ConfirmDialog';

const BANK_ACCOUNT = { bank: '국민은행', number: '703001-01-598295', holder: '티앤티 주식회사' };

const MONTHLY_PRICE = 49000;
const PERIOD_OPTIONS = [
  { months: 1,  label: '1개월', price: 49000,  discount: null },
  { months: 3,  label: '3개월', price: 147000, discount: null },
  { months: 6,  label: '6개월', price: 294000, discount: null },
  { months: 12, label: '1년',   price: 490000, discount: '2개월 무료 혜택' },
];

const PLAN_LABELS = { trial: '무료체험', active: '이용중', suspended: '정지' };

const REQUEST_STATUS = {
  pending:   { label: '확인 대기중', color: 'text-yellow-600 dark:text-yellow-400', icon: Clock },
  confirmed: { label: '확인 완료',  color: 'text-green-600 dark:text-green-400',  icon: CheckCircle },
  rejected:  { label: '거절됨',     color: 'text-red-600 dark:text-red-400',      icon: XCircle },
};

const DAYS = [
  { key: 'mon', label: '월' },
  { key: 'tue', label: '화' },
  { key: 'wed', label: '수' },
  { key: 'thu', label: '목' },
  { key: 'fri', label: '금' },
  { key: 'sat', label: '토' },
  { key: 'sun', label: '일' },
];

const DEFAULT_HOURS = {
  weekday: { open: '09:00', close: '22:00', closed: false },
  saturday: { open: '09:00', close: '18:00', closed: false },
  sunday: { open: '09:00', close: '18:00', closed: true },
};

function hoursToCustom(hours) {
  return {
    mon: { ...hours.weekday },
    tue: { ...hours.weekday },
    wed: { ...hours.weekday },
    thu: { ...hours.weekday },
    fri: { ...hours.weekday },
    sat: { ...hours.saturday },
    sun: { ...hours.sunday },
  };
}

function parseBusinessHours(raw) {
  if (!raw) return { hours: DEFAULT_HOURS, custom: false, customHours: hoursToCustom(DEFAULT_HOURS), notice: '' };
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (parsed.custom) {
      const customHours = {
        mon: parsed.mon || DEFAULT_HOURS.weekday,
        tue: parsed.tue || DEFAULT_HOURS.weekday,
        wed: parsed.wed || DEFAULT_HOURS.weekday,
        thu: parsed.thu || DEFAULT_HOURS.weekday,
        fri: parsed.fri || DEFAULT_HOURS.weekday,
        sat: parsed.sat || DEFAULT_HOURS.saturday,
        sun: parsed.sun || DEFAULT_HOURS.sunday,
      };
      return { hours: DEFAULT_HOURS, custom: true, customHours, notice: parsed.notice || '' };
    }
    const hours = {
      weekday: parsed.weekday || DEFAULT_HOURS.weekday,
      saturday: parsed.saturday || DEFAULT_HOURS.saturday,
      sunday: parsed.sunday || DEFAULT_HOURS.sunday,
    };
    return { hours, custom: false, customHours: hoursToCustom(hours), notice: parsed.notice || '' };
  } catch {
    return { hours: DEFAULT_HOURS, custom: false, customHours: hoursToCustom(DEFAULT_HOURS), notice: '' };
  }
}

function TimeRow({ label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">{label}</span>
        <button
          type="button"
          onClick={() => onChange({ ...value, closed: !value.closed })}
          className="flex items-center gap-2"
        >
          <span className={`text-xs ${value.closed ? 'text-red-500 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
            {value.closed ? '휴무' : '운영'}
          </span>
          <div className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${value.closed ? 'bg-gray-300 dark:bg-gray-600' : 'bg-green-500'}`}>
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${value.closed ? 'translate-x-0.5' : 'translate-x-5'}`} />
          </div>
        </button>
      </div>
      {!value.closed && (
        <div className="flex items-center gap-2">
          <input
            type="time"
            className="input py-1.5 text-sm flex-1"
            value={value.open}
            onChange={e => onChange({ ...value, open: e.target.value })}
          />
          <span className="text-gray-400 text-sm">~</span>
          <input
            type="time"
            className="input py-1.5 text-sm flex-1"
            value={value.close}
            onChange={e => onChange({ ...value, close: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="card p-6">
    <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
      {title}
    </h2>
    {children}
  </div>
);

function KioskPinSection({ gymId }) {
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'ok'|'err', text }

  async function handleSave(e) {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) { setMsg({ type: 'err', text: '숫자 4자리로 입력하세요.' }); return; }
    if (pin !== confirm) { setMsg({ type: 'err', text: 'PIN이 일치하지 않습니다.' }); return; }
    setSaving(true);
    setMsg(null);
    const { error } = await supabase.rpc('owner_update_kiosk_pin', { p_gym_id: gymId, p_new_pin: pin });
    setSaving(false);
    if (error) { setMsg({ type: 'err', text: '저장 실패: ' + error.message }); }
    else { setMsg({ type: 'ok', text: '키오스크 PIN이 변경되었습니다.' }); setPin(''); setConfirm(''); }
  }

  return (
    <div className="card p-6">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
        <Lock className="w-4 h-4 text-gray-400" />
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">키오스크 PIN</h2>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        키오스크 화면의 설정 메뉴 진입 시 사용하는 4자리 PIN입니다. 기본값은 <span className="font-mono font-bold">0000</span>입니다.
      </p>
      <form onSubmit={handleSave} className="flex items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">새 PIN</label>
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setMsg(null); }}
            placeholder="••••"
            className="input text-center font-mono tracking-widest text-lg"
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">PIN 확인</label>
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={confirm}
            onChange={e => { setConfirm(e.target.value.replace(/\D/g, '')); setMsg(null); }}
            placeholder="••••"
            className="input text-center font-mono tracking-widest text-lg"
          />
        </div>
        <button
          type="submit"
          disabled={saving || pin.length !== 4 || confirm.length !== 4}
          className="btn-primary disabled:opacity-40 flex-shrink-0"
        >
          {saving ? '저장 중...' : '변경'}
        </button>
      </form>
      {msg && (
        <p className={`text-xs mt-2 ${msg.type === 'ok' ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
          {msg.text}
        </p>
      )}
    </div>
  );
}

const Settings = () => {
  const { gymSettings, updateGymSettings, exportBackup, importBackup, exportMembersCSV, resetAllData, members, attendances, memberships, ptAssignments, isAdminMode, approveMember, rejectMember } = useApp();
  const { gym, profile, isDemoMode } = useAuth();
  const [form, setForm] = useState({
    name: gymSettings.name || '',
    phone: gymSettings.phone || '',
    address: gymSettings.address || '',
  });
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetNameInput, setResetNameInput] = useState('');
  const fileInputRef = useRef();

  // Operating hours state
  const initialParsed = parseBusinessHours(gymSettings.businessHours);
  const [hours, setHours] = useState(initialParsed.hours);
  const [custom, setCustom] = useState(initialParsed.custom);
  const [customHours, setCustomHours] = useState(initialParsed.customHours);
  const [hoursNotice, setHoursNotice] = useState(initialParsed.notice);
  const [hoursSaving, setHoursSaving] = useState(false);

  // Plan & payment state
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [paymentForm, setPaymentForm] = useState({ periodMonths: 1, senderName: '', note: '' });
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const planDaysLeft = gym?.plan_expires_at
    ? differenceInDays(parseISO(gym.plan_expires_at), new Date())
    : null;
  const hasPendingRequest = paymentRequests.some(r => r.status === 'pending');

  // Push notification state
  const [pushSupported] = useState(() => isPushSupported());
  const [pushPermission, setPushPermission] = useState(() => getNotificationPermission());
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  // Locker settings state
  const [lockerCfg, setLockerCfg] = useState(() => ({
    enabled: gymSettings.lockerSettings?.enabled ?? false,
    isFree: gymSettings.lockerSettings?.isFree ?? true,
    monthlyFee: gymSettings.lockerSettings?.monthlyFee ?? 0,
    periods: gymSettings.lockerSettings?.periods ?? [1, 3, 6, 12],
  }));
  const [lockerSaving, setLockerSaving] = useState(false);

  const PERIOD_MONTHS = [1, 3, 6, 12];

  const handleLockerSave = async () => {
    setLockerSaving(true);
    await updateGymSettings({ lockerSettings: lockerCfg });
    setLockerSaving(false);
  };

  // Notices state
  const [notices, setNotices] = useState([]);
  const [noticeForm, setNoticeForm] = useState({
    title: '', content: '', startsAt: new Date().toISOString().split('T')[0], endsAt: '',
  });
  const [noticeAdding, setNoticeAdding] = useState(false);
  const [showNoticeForm, setShowNoticeForm] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);

  useEffect(() => {
    const p = parseBusinessHours(gymSettings.businessHours);
    setHours(p.hours);
    setCustom(p.custom);
    setCustomHours(p.customHours);
    setHoursNotice(p.notice);
  }, [gymSettings.businessHours]);

  useEffect(() => {
    setForm({ name: gymSettings.name || '', phone: gymSettings.phone || '', address: gymSettings.address || '' });
  }, [gymSettings.name, gymSettings.phone, gymSettings.address]);

  useEffect(() => {
    if (gym?.id && !isDemoMode) {
      getAllGymNotices(gym.id).then(setNotices);
      getMyPaymentRequests(gym.id).then(setPaymentRequests);
      getCurrentSubscription().then(sub => setPushSubscribed(!!sub));
    }
  }, [gym?.id, isDemoMode]);

  const handlePushSubscribe = async () => {
    if (!gym?.id) return;
    setPushLoading(true);
    const { error } = await requestAndSubscribe(gym.id);
    if (!error) {
      setPushSubscribed(true);
      setPushPermission('granted');
    } else if (error === 'denied') {
      setPushPermission('denied');
    }
    setPushLoading(false);
  };

  const handlePushUnsubscribe = async () => {
    if (!gym?.id) return;
    setPushLoading(true);
    await unsubscribeFromPush(gym.id);
    setPushSubscribed(false);
    setPushLoading(false);
  };

  function handleGroupChange(key, value) {
    const updated = { ...hours, [key]: value };
    setHours(updated);
    setCustomHours(hoursToCustom(updated));
  }

  function handleCustomChange(day, value) {
    setCustomHours(prev => ({ ...prev, [day]: value }));
  }

  function toggleCustom() {
    if (!custom) setCustomHours(hoursToCustom(hours));
    setCustom(!custom);
  }

  function buildHoursPayload() {
    const base = custom ? { custom: true, ...customHours } : { custom: false, ...hours };
    if (hoursNotice.trim()) base.notice = hoursNotice.trim();
    return base;
  }

  const handleInfoSave = (e) => {
    e.preventDefault();
    updateGymSettings(form);
  };

  const handleHoursSave = async (e) => {
    e.preventDefault();
    setHoursSaving(true);
    await updateGymSettings({ businessHours: JSON.stringify(buildHoursPayload()) });
    setHoursSaving(false);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentForm.senderName.trim()) return;
    setPaymentError('');
    setPaymentSubmitting(true);
    const { error } = await submitPaymentRequest({
      gymId: gym.id,
      periodMonths: paymentForm.periodMonths,
      senderName: paymentForm.senderName.trim(),
      note: paymentForm.note.trim() || null,
    });
    if (error) {
      setPaymentError(error.message?.includes('pending') ? '이미 대기중인 요청이 있습니다.' : '요청 중 오류가 발생했습니다.');
    } else {
      const updated = await getMyPaymentRequests(gym.id);
      setPaymentRequests(updated);
      setPaymentForm({ periodMonths: 1, senderName: '', note: '' });
    }
    setPaymentSubmitting(false);
  };

  const handleNoticeAdd = async (e) => {
    e.preventDefault();
    if (!noticeForm.title.trim() || !gym?.id) return;
    setNoticeAdding(true);
    if (editingNotice) {
      const { error } = await updateGymNotice(editingNotice.id, {
        title: noticeForm.title.trim(),
        content: noticeForm.content.trim() || null,
        startsAt: noticeForm.startsAt,
        endsAt: noticeForm.endsAt || null,
      });
      if (!error) {
        const updated = await getAllGymNotices(gym.id);
        setNotices(updated);
        setNoticeForm({ title: '', content: '', startsAt: new Date().toISOString().split('T')[0], endsAt: '' });
        setShowNoticeForm(false);
        setEditingNotice(null);
      }
    } else {
      const { error } = await createGymNotice({
        gymId: gym.id,
        title: noticeForm.title.trim(),
        content: noticeForm.content.trim() || null,
        startsAt: noticeForm.startsAt,
        endsAt: noticeForm.endsAt || null,
      });
      if (!error) {
        const updated = await getAllGymNotices(gym.id);
        setNotices(updated);
        setNoticeForm({ title: '', content: '', startsAt: new Date().toISOString().split('T')[0], endsAt: '' });
        setShowNoticeForm(false);
      }
    }
    setNoticeAdding(false);
  };

  const handleNoticeDelete = async (id) => {
    await deleteGymNotice(id);
    setNotices(prev => prev.filter(n => n.id !== id));
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRestoreFile(file);
    }
    e.target.value = '';
  };

  const stats = [
    { label: '전체 회원', value: members.length },
    { label: '총 출석', value: attendances.length },
    { label: '발급 회원권', value: memberships.length },
    { label: '진행 PT', value: ptAssignments.filter(p => p.status === 'active').length },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      {/* 가입 승인 대기 */}
      {!isDemoMode && (() => {
        const pending = members.filter(m => m.status === 'pending');
        if (!pending.length) return null;
        return (
          <div className="card p-6 border-l-4 border-yellow-400">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
              <Users className="w-4 h-4 text-yellow-500" />
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                가입 승인 대기
              </h2>
              <span className="ml-1 px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400 text-xs font-bold">
                {pending.length}명
              </span>
            </div>
            <div className="space-y-2">
              {pending.map(m => (
                <div key={m.id} className="flex items-center justify-between py-2.5 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{m.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{m.phone}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => approveMember(m.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors"
                    >
                      <UserCheck className="w-3.5 h-3.5" />
                      승인
                    </button>
                    <button
                      onClick={() => rejectMember(m.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 text-xs font-medium transition-colors"
                    >
                      <UserX className="w-3.5 h-3.5" />
                      거절
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* 데이터 현황 */}
      <div className="grid grid-cols-4 gap-3">
        {stats.map(s => (
          <div key={s.label} className="card p-4 text-center">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{s.value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* 매장 코드 */}
      {gym?.code && (
        <div className="card p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            매장 코드 &amp; 초대 링크
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">회원이 앱 가입 시 입력하는 코드입니다. 링크를 공유하면 코드 없이 바로 가입할 수 있습니다.</p>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 bg-gray-50 dark:bg-gray-700/50 rounded-xl px-5 py-4 text-center">
              <span className="text-3xl font-mono font-bold tracking-widest text-primary-600 dark:text-primary-400 select-all">
                {gym.code}
              </span>
            </div>
            <button
              onClick={() => navigator.clipboard.writeText(gym.code)}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <Copy className="w-4 h-4" /> 코드 복사
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-gray-50 dark:bg-gray-700/50 rounded-xl px-3 py-2">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">회원 가입 링크</p>
              <p className="text-xs text-gray-700 dark:text-gray-300 font-mono truncate select-all">
                {window.location.origin}/?member_code={gym.code}
              </p>
            </div>
            <button
              onClick={() => navigator.clipboard.writeText(`${window.location.origin}/?member_code=${gym.code}`)}
              className="btn-secondary flex items-center gap-2 text-sm flex-shrink-0"
            >
              <Copy className="w-4 h-4" /> 링크 복사
            </button>
          </div>
        </div>
      )}

      {/* 키오스크 PIN */}
      {gym?.id && !isDemoMode && (
        <KioskPinSection gymId={gym.id} />
      )}

      {/* 알림 설정 */}
      {gym?.id && !isDemoMode && pushSupported && (
        <Section title="알림 설정">
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${pushSubscribed ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-gray-200 dark:bg-gray-600'}`}>
                {pushSubscribed ? (
                  <Bell className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                ) : (
                  <BellOff className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {pushSubscribed ? '알림 활성화됨' : '알림 비활성화됨'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  납부 확인, 만료 임박 등의 알림을 받습니다
                </p>
                {pushPermission === 'denied' && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                    브라우저에서 알림이 차단되어 있습니다. 브라우저 설정에서 허용해주세요.
                  </p>
                )}
              </div>
            </div>
            {pushPermission !== 'denied' && (
              pushSubscribed ? (
                <button
                  onClick={handlePushUnsubscribe}
                  disabled={pushLoading}
                  className="btn-secondary text-sm flex-shrink-0"
                >
                  {pushLoading ? '처리 중...' : '알림 끄기'}
                </button>
              ) : (
                <button
                  onClick={handlePushSubscribe}
                  disabled={pushLoading}
                  className="btn-primary text-sm flex-shrink-0"
                >
                  {pushLoading ? '처리 중...' : '알림 켜기'}
                </button>
              )
            )}
          </div>
        </Section>
      )}

      {/* 플랜 & 납부 */}
      {gym?.id && !isDemoMode && (
        <Section title="플랜 & 납부">
          {/* 현재 플랜 상태 */}
          <div className={`flex items-center justify-between p-4 rounded-xl mb-4 ${
            planDaysLeft !== null && planDaysLeft <= 7
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              : 'bg-gray-50 dark:bg-gray-700/50'
          }`}>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">현재 플랜</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {PLAN_LABELS[gym.plan] || gym.plan || '없음'}
              </p>
            </div>
            {gym.plan_expires_at && (
              <div className="text-right">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">만료일</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {format(parseISO(gym.plan_expires_at), 'yyyy.MM.dd', { locale: ko })}
                </p>
                {planDaysLeft !== null && (
                  <p className={`text-xs font-bold mt-0.5 ${
                    planDaysLeft < 0 ? 'text-red-600 dark:text-red-400' :
                    planDaysLeft <= 7 ? 'text-red-500 dark:text-red-400' :
                    planDaysLeft <= 30 ? 'text-yellow-600 dark:text-yellow-400' :
                    'text-gray-400'
                  }`}>
                    {planDaysLeft < 0 ? '만료됨' : `D-${planDaysLeft}`}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* 계좌 안내 */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800 mb-4">
            <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2">
              <CreditCard className="w-3.5 h-3.5 inline mr-1" />
              납부 방법
            </p>
            <div className="space-y-1 mb-2">
              <p className="text-xs text-blue-600 dark:text-blue-400">① 아래 계좌로 입금해주세요.</p>
              <p className="text-sm font-mono font-bold text-blue-800 dark:text-blue-300">
                {BANK_ACCOUNT.bank} {BANK_ACCOUNT.number}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">예금주: {BANK_ACCOUNT.holder}</p>
            </div>
            {(() => {
              const opt = PERIOD_OPTIONS.find(o => o.months === paymentForm.periodMonths);
              return opt ? (
                <p className="text-sm font-bold text-blue-800 dark:text-blue-200 mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                  입금 금액: {opt.price.toLocaleString('ko-KR')}원
                  {opt.discount && <span className="ml-2 text-xs font-normal text-yellow-600 dark:text-yellow-400">({opt.discount} 적용)</span>}
                </p>
              ) : null;
            })()}
            <p className="text-xs text-blue-500 dark:text-blue-400 mt-2">② 입금 후 아래에서 납부 확인 요청을 해주세요.</p>
          </div>

          {/* 납부 확인 요청 폼 */}
          {hasPendingRequest ? (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800 flex items-start gap-3 mb-4">
              <Clock className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">납부 확인 요청 대기중</p>
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">
                  관리자가 입금을 확인하면 플랜이 자동으로 연장됩니다.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handlePaymentSubmit} className="space-y-3 mb-4">
              <div>
                <p className="label mb-2">연장 기간</p>
                <div className="grid grid-cols-2 gap-2">
                  {PERIOD_OPTIONS.map(({ months, label, price, discount }) => (
                    <button
                      key={months}
                      type="button"
                      onClick={() => setPaymentForm(p => ({ ...p, periodMonths: months }))}
                      className={`relative px-4 py-3 rounded-xl text-sm font-medium border transition-colors text-left ${
                        paymentForm.periodMonths === months
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {discount && (
                        <span className="absolute -top-2 -right-1 text-xs px-1.5 py-0.5 rounded-full font-bold bg-yellow-400 text-yellow-900">
                          {discount}
                        </span>
                      )}
                      <span className="block font-semibold">{label}</span>
                      <span className={`text-xs mt-0.5 block ${paymentForm.periodMonths === months ? 'text-primary-100' : 'text-gray-400 dark:text-gray-500'}`}>
                        {price.toLocaleString('ko-KR')}원
                        {discount && <span className="ml-1 line-through opacity-60">{(MONTHLY_PRICE * months).toLocaleString('ko-KR')}원</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">입금자명 <span className="text-red-500">*</span></label>
                <input
                  className="input"
                  placeholder="이체 시 입력한 입금자명"
                  value={paymentForm.senderName}
                  onChange={e => setPaymentForm(p => ({ ...p, senderName: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="label">요청사항 <span className="text-xs text-gray-400 font-normal">(선택)</span></label>
                <input
                  className="input"
                  placeholder="특이사항이 있으면 입력해주세요"
                  value={paymentForm.note}
                  onChange={e => setPaymentForm(p => ({ ...p, note: e.target.value }))}
                />
              </div>
              {paymentError && (
                <p className="text-sm text-red-600 dark:text-red-400">{paymentError}</p>
              )}
              <button
                type="submit"
                className="btn-primary w-full justify-center"
                disabled={paymentSubmitting || !paymentForm.senderName.trim()}
              >
                {paymentSubmitting ? '요청 중...' : '납부 확인 요청'}
              </button>
            </form>
          )}

          {/* 요청 이력 */}
          {paymentRequests.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">요청 이력</p>
              <div className="space-y-2">
                {paymentRequests.map(req => {
                  const cfg = REQUEST_STATUS[req.status] || REQUEST_STATUS.pending;
                  const Icon = cfg.icon;
                  return (
                    <div key={req.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-xs gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {req.period_months}개월 연장 · {req.sender_name}
                        </p>
                        <p className="text-gray-400 mt-0.5">
                          {req.created_at ? format(parseISO(req.created_at), 'yyyy.MM.dd HH:mm', { locale: ko }) : ''}
                          {req.admin_note && <span className="ml-1">· {req.admin_note}</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {req.status === 'confirmed' && (
                          <button
                            onClick={() => printPaymentReceipt({
                              gymName: gymSettings.name || gym?.name,
                              ownerName: profile?.name,
                              ownerEmail: profile?.email,
                              periodMonths: req.period_months,
                              senderName: req.sender_name,
                              confirmedAt: req.confirmed_at,
                              requestId: req.id,
                            })}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="청구서 출력"
                          >
                            <Printer className="w-3.5 h-3.5" />
                            영수증
                          </button>
                        )}
                        <span className={`flex items-center gap-1 font-medium ${cfg.color}`}>
                          <Icon className="w-3.5 h-3.5" />
                          {cfg.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* 헬스장 기본 정보 */}
      <Section title="헬스장 정보">
        <form onSubmit={handleInfoSave} className="space-y-4">
          <div>
            <label className="label flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" /> 헬스장 이름
            </label>
            <input
              className="input"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="My GYM"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" /> 전화번호
              </label>
              <input
                className="input"
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="02-0000-0000"
              />
            </div>
          </div>
          <div>
            <label className="label flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" /> 주소
            </label>
            <div className="flex gap-2">
              <input
                className="input flex-1"
                value={form.address}
                onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                placeholder="서울시 강남구 테헤란로..."
                readOnly={!!window.daum}
                onClick={() => {
                  if (!window.daum) return;
                  new window.daum.Postcode({
                    oncomplete: (data) => setForm(p => ({ ...p, address: data.roadAddress || data.jibunAddress })),
                  }).open();
                }}
              />
              {window.daum && (
                <button
                  type="button"
                  onClick={() => {
                    new window.daum.Postcode({
                      oncomplete: (data) => setForm(p => ({ ...p, address: data.roadAddress || data.jibunAddress })),
                    }).open();
                  }}
                  className="btn-secondary text-sm flex-shrink-0"
                >
                  주소 검색
                </button>
              )}
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" className="btn-primary">저장</button>
          </div>
        </form>
      </Section>

      {/* 운영시간 */}
      <Section title="운영시간">
        <form onSubmit={handleHoursSave} className="space-y-4">
          <div className="space-y-3">
            <TimeRow label="평일 (월~금)" value={hours.weekday} onChange={v => handleGroupChange('weekday', v)} />
            <TimeRow label="토요일" value={hours.saturday} onChange={v => handleGroupChange('saturday', v)} />
            <TimeRow label="일요일" value={hours.sunday} onChange={v => handleGroupChange('sunday', v)} />
          </div>
          <button
            type="button"
            onClick={toggleCustom}
            className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
          >
            {custom ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {custom ? '개별 설정 닫기' : '요일별 개별 설정'}
          </button>
          {custom && (
            <div className="space-y-2.5 pl-1 border-l-2 border-primary-200 dark:border-primary-800">
              {DAYS.map(d => (
                <TimeRow
                  key={d.key}
                  label={d.label + '요일'}
                  value={customHours[d.key]}
                  onChange={v => handleCustomChange(d.key, v)}
                />
              ))}
            </div>
          )}
          <div>
            <p className="label mb-1">특별 안내 <span className="text-xs text-gray-400 font-normal">(선택)</span></p>
            <textarea
              className="input resize-none"
              rows={3}
              placeholder={"예) 매달 둘째 주 일요일 휴무\n명절 연휴 (설, 추석) 휴무"}
              value={hoursNotice}
              onChange={e => setHoursNotice(e.target.value)}
            />
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" className="btn-primary" disabled={hoursSaving}>
              {hoursSaving ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </Section>

      {/* 헬스장 공지 */}
      {gym?.id && (
        <Section title="헬스장 공지">
          <div className="space-y-3">
            {notices.length === 0 && !showNoticeForm && (
              <p className="text-sm text-gray-400 text-center py-4">등록된 공지가 없습니다.</p>
            )}
            {notices.map(n => (
              <div key={n.id} className="flex items-start justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{n.title}</p>
                  {n.content && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{n.content}</p>}
                  <p className="text-xs text-gray-400 mt-1">
                    {n.starts_at}{n.ends_at ? ` ~ ${n.ends_at}` : ' ~'}
                  </p>
                </div>
                <div className="ml-3 flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => {
                      setEditingNotice(n);
                      setNoticeForm({ title: n.title, content: n.content || '', startsAt: n.starts_at, endsAt: n.ends_at || '' });
                      setShowNoticeForm(true);
                    }}
                    className="p-1.5 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  >
                    <EditIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleNoticeDelete(n.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {showNoticeForm ? (
              <form onSubmit={handleNoticeAdd} className="space-y-3 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-200 dark:border-primary-800">
                <input
                  className="input"
                  placeholder="공지 제목"
                  value={noticeForm.title}
                  onChange={e => setNoticeForm(p => ({ ...p, title: e.target.value }))}
                  required
                />
                <textarea
                  className="input resize-none"
                  rows={3}
                  placeholder="공지 내용 (선택)"
                  value={noticeForm.content}
                  onChange={e => setNoticeForm(p => ({ ...p, content: e.target.value }))}
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label text-xs">시작일</label>
                    <input
                      type="date"
                      className="input"
                      value={noticeForm.startsAt}
                      onChange={e => setNoticeForm(p => ({ ...p, startsAt: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="label text-xs">종료일 <span className="text-gray-400 font-normal">(선택)</span></label>
                    <input
                      type="date"
                      className="input"
                      value={noticeForm.endsAt}
                      onChange={e => setNoticeForm(p => ({ ...p, endsAt: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="btn-primary text-sm flex-1" disabled={noticeAdding}>
                    {noticeAdding ? '저장 중...' : editingNotice ? '수정' : '공지 등록'}
                  </button>
                  <button type="button" onClick={() => { setShowNoticeForm(false); setEditingNotice(null); setNoticeForm({ title: '', content: '', startsAt: new Date().toISOString().split('T')[0], endsAt: '' }); }} className="btn-secondary text-sm">
                    취소
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setShowNoticeForm(true)}
                className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                <Plus className="w-4 h-4" />
                공지 추가
              </button>
            )}
          </div>
        </Section>
      )}

      {/* 락커 설정 */}
      <Section title="락커 설정">
        <div className="space-y-5">
          {/* 운영 여부 */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">락커 운영</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">비활성화 시 락커 메뉴는 기존대로 수동 운영됩니다</p>
            </div>
            <button
              type="button"
              onClick={() => setLockerCfg(p => ({ ...p, enabled: !p.enabled }))}
              className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${lockerCfg.enabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${lockerCfg.enabled ? 'translate-x-7' : 'translate-x-1'}`} />
            </button>
          </div>

          {lockerCfg.enabled && (
            <>
              {/* 요금 방식 */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">요금 방식</p>
                <div className="flex gap-3">
                  {[{ label: '무료', value: true }, { label: '유료', value: false }].map(opt => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setLockerCfg(p => ({ ...p, isFree: opt.value }))}
                      className={`flex-1 py-2 rounded-xl border text-sm font-medium transition-colors ${
                        lockerCfg.isFree === opt.value
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 월 요금 */}
              {!lockerCfg.isFree && (
                <div>
                  <label className="label">월 요금 (원)</label>
                  <input
                    type="number"
                    className="input"
                    placeholder="10000"
                    value={lockerCfg.monthlyFee}
                    onChange={e => setLockerCfg(p => ({ ...p, monthlyFee: Number(e.target.value) }))}
                    min="0"
                  />
                </div>
              )}

              {/* 기간 옵션 */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">기간 옵션</p>
                <div className="flex gap-2 flex-wrap">
                  {PERIOD_MONTHS.map(m => {
                    const active = lockerCfg.periods.includes(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setLockerCfg(p => ({
                          ...p,
                          periods: active
                            ? p.periods.filter(x => x !== m)
                            : [...p.periods, m].sort((a, b) => a - b),
                        }))}
                        className={`px-4 py-1.5 rounded-xl border text-sm font-medium transition-colors ${
                          active
                            ? 'bg-primary-600 border-primary-600 text-white'
                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {m}개월
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-2">락커 배정 시 선택 가능한 기간입니다</p>
              </div>
            </>
          )}

          <div className="flex justify-end pt-1">
            <button onClick={handleLockerSave} disabled={lockerSaving} className="btn-primary">
              {lockerSaving ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </Section>

      {/* 데이터 내보내기 */}
      <Section title="데이터 내보내기">
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">회원 목록 CSV</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">엑셀에서 열 수 있는 형식으로 내보내기</p>
            </div>
            <button onClick={exportMembersCSV} className="btn-secondary flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4" />
              다운로드
            </button>
          </div>
          <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">전체 데이터 백업 (JSON)</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">모든 데이터를 파일로 저장 — 정기적으로 백업 권장</p>
            </div>
            <button onClick={exportBackup} className="btn-primary flex items-center gap-2 text-sm">
              <Download className="w-4 h-4" />
              백업
            </button>
          </div>
        </div>
      </Section>

      {/* 데이터 복원 */}
      <Section title="데이터 복원">
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">복원 시 현재 데이터가 덮어씌워집니다.</p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">복원 전에 현재 데이터를 먼저 백업하세요.</p>
            </div>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <Upload className="w-4 h-4" />
            백업 파일 선택
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleFileImport}
          />
        </div>
      </Section>

      {/* 위험 구역 — 어드민 모드에서만 표시 */}
      {isAdminMode && (
        <Section title="위험 구역">
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-300">전체 데이터 초기화</p>
                <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">모든 데이터가 삭제됩니다. 되돌릴 수 없습니다.</p>
              </div>
              <button
                onClick={() => { setResetConfirm(true); setResetNameInput(''); }}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                초기화
              </button>
            </div>
          </div>
        </Section>
      )}

      {/* 이중 확인 모달 */}
      {resetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white text-center mb-2">전체 데이터 초기화</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
              이 작업은 <span className="font-semibold text-red-600">되돌릴 수 없습니다.</span><br />
              확인을 위해 헬스장 이름을 입력해주세요.
            </p>
            <p className="text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg px-3 py-2 text-center mb-3 select-all">
              {gymSettings.name || 'GYM Manager'}
            </p>
            <input
              className="input mb-4"
              placeholder="헬스장 이름 입력"
              value={resetNameInput}
              onChange={e => setResetNameInput(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setResetConfirm(false); setResetNameInput(''); }}
                className="btn-secondary flex-1"
              >
                취소
              </button>
              <button
                onClick={() => { resetAllData(); setResetConfirm(false); setResetNameInput(''); }}
                disabled={resetNameInput !== (gymSettings.name || 'GYM Manager')}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                초기화
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={!!restoreFile}
        title="백업 복원"
        message="백업 데이터를 복원하면 현재 데이터가 덮어씌워집니다. 계속하시겠습니까?"
        onConfirm={() => { importBackup(restoreFile); setRestoreFile(null); }}
        onCancel={() => setRestoreFile(null)}
      />
    </div>
  );
};

export default Settings;
